import {
  DEFAULT_WORKER_WINDOW_SIZE,
  HEADER_HEIGHT,
  MIN_WINDOW_SIZE,
  PHONE_BEZEL_WIDTH,
  STALE_CLEANUP_MS,
} from "../constants";
import type { ImpersonationSession, ImpersonationSessionType, PersistedSession } from "../types";

const STORAGE_KEY = "impersonation-sessions";
const BALLOON_WIDTH = 220;
const BALLOON_HEIGHT = 36;
const BALLOON_BOTTOM_OFFSET = 80;
const BALLOON_SPACING = 50;
const BALLOON_RIGHT_OFFSET = 20;

let sessionCounter = 0;
export let zIndexCounter = 0;

export function nextZIndex(): number {
  return zIndexCounter++;
}

export function generateSessionId(): string {
  sessionCounter += 1;
  return `impersonation-${Date.now()}-${sessionCounter}`;
}

export function getInitialBalloonPosition(index: number): { x: number; y: number } {
  return {
    x: window.innerWidth - BALLOON_WIDTH - BALLOON_RIGHT_OFFSET,
    y: window.innerHeight - BALLOON_BOTTOM_OFFSET - index * BALLOON_SPACING,
  };
}

export function getDefaultWindowSize(sessionType: ImpersonationSessionType): {
  width: number;
  height: number;
} {
  if (sessionType === "worker") {
    return { ...DEFAULT_WORKER_WINDOW_SIZE };
  }

  // Workplace sessions open at the full available viewport so admins can use
  // the impersonated app at full fidelity. Width has no chrome to subtract;
  // height reserves room for the floating header.
  return {
    width: window.innerWidth,
    height: Math.max(MIN_WINDOW_SIZE.height, window.innerHeight - HEADER_HEIGHT),
  };
}

export function getInitialWindowPosition(
  sessionType: ImpersonationSessionType,
  sessionCount: number,
  windowSize: { width: number; height: number }
): { x: number; y: number } {
  if (sessionType === "workplace") {
    return { x: 0, y: 0 };
  }

  const offset = sessionCount * 30;
  return {
    x: Math.max(50, window.innerWidth / 2 - windowSize.width / 2 + offset),
    y: Math.max(50, 100 + offset),
  };
}

/**
 * Worker frames render with a phone bezel and a header above the iframe.
 * Workplace frames are headerless boxes (the floating header sits above the
 * iframe and is included in `position.y`-relative sizing).
 */
function getOuterFrameSize(session: ImpersonationSession): { width: number; height: number } {
  if (session.sessionType === "worker") {
    const bezel = PHONE_BEZEL_WIDTH;
    return {
      width: session.size.width + bezel * 2,
      height: session.size.height + bezel * 2 + HEADER_HEIGHT,
    };
  }

  return {
    width: session.size.width,
    height: session.size.height + HEADER_HEIGHT,
  };
}

export function clampWindowPosition(
  session: ImpersonationSession,
  viewportWidth: number,
  viewportHeight: number
): { x: number; y: number } {
  const { width, height } = getOuterFrameSize(session);
  return {
    x: Math.max(0, Math.min(session.position.x, viewportWidth - width)),
    y: Math.max(0, Math.min(session.position.y, viewportHeight - height)),
  };
}

/**
 * Clamps an outer (header-included) rectangle to fit inside the viewport.
 * Returns the inner-iframe size (excludes the header) and the new x/y origin
 * so callers can write straight back into `session.size` and `session.position`.
 */
export function clampWindowRect(
  sessionType: ImpersonationSessionType,
  position: { x: number; y: number },
  size: { width: number; height: number },
  viewportWidth: number,
  viewportHeight: number
): { position: { x: number; y: number }; size: { width: number; height: number } } {
  const bezel = sessionType === "worker" ? PHONE_BEZEL_WIDTH : 0;
  const chromeWidth = bezel * 2;
  const chromeHeight = bezel * 2 + HEADER_HEIGHT;

  // Cap the inner size to what fits inside the viewport given the origin.
  const maxInnerWidth = Math.max(MIN_WINDOW_SIZE.width, viewportWidth - position.x - chromeWidth);
  const maxInnerHeight = Math.max(
    MIN_WINDOW_SIZE.height,
    viewportHeight - position.y - chromeHeight
  );

  const clampedSize = {
    width: Math.max(MIN_WINDOW_SIZE.width, Math.min(size.width, maxInnerWidth)),
    height: Math.max(MIN_WINDOW_SIZE.height, Math.min(size.height, maxInnerHeight)),
  };

  // Snap origin so the outer rect stays inside the viewport.
  const outerWidth = clampedSize.width + chromeWidth;
  const outerHeight = clampedSize.height + chromeHeight;
  const clampedPosition = {
    x: Math.max(0, Math.min(position.x, viewportWidth - outerWidth)),
    y: Math.max(0, Math.min(position.y, viewportHeight - outerHeight)),
  };

  return { position: clampedPosition, size: clampedSize };
}

export function clampBalloonPosition(
  balloonPosition: { x: number; y: number },
  viewportWidth: number,
  viewportHeight: number
): { x: number; y: number } {
  return {
    x: Math.max(0, Math.min(balloonPosition.x, viewportWidth - BALLOON_WIDTH)),
    y: Math.max(0, Math.min(balloonPosition.y, viewportHeight - BALLOON_HEIGHT)),
  };
}

export function persistSessions(sessions: ImpersonationSession[]): void {
  const toPersist: PersistedSession[] = sessions.map((session) => ({
    id: session.id,
    sessionType: session.sessionType,
    targetId: session.targetId,
    targetName: session.targetName,
    cognitoUsername: session.cognitoUsername,
    url: session.url,
    startedAt: session.startedAt,
    expiresAt: session.expiresAt,
  }));

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toPersist));
  } catch {
    // localStorage may be full or disabled — silently ignore
  }
}

/**
 * Legacy persisted sessions may have `workerId`/`workerName` instead of
 * `targetId`/`targetName` and no `sessionType`. This interface captures those
 * old shapes so we can migrate them on load.
 */
interface LegacyPersistedSession {
  id: string;
  sessionType?: ImpersonationSessionType;
  targetId?: string;
  targetName?: string;
  workerId?: string;
  workerName?: string;
  cognitoUsername: string;
  url: string;
  startedAt: number;
  expiresAt?: number;
}

export function loadPersistedSessions(): ImpersonationSession[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw) as LegacyPersistedSession[];
    const now = Date.now();

    const valid = parsed
      .filter((session) => now - session.startedAt < STALE_CLEANUP_MS)
      .reduce<
        Array<{
          sessionType: ImpersonationSessionType;
          targetId: string;
          targetName: string;
          session: LegacyPersistedSession;
        }>
      >((accumulator, session) => {
        const sessionType: ImpersonationSessionType = session.sessionType ?? "worker";
        const targetId = session.targetId ?? session.workerId;
        const targetName = session.targetName ?? session.workerName;
        if (!targetId || !targetName) {
          return accumulator;
        }
        try {
          new URL(session.url);
        } catch {
          return accumulator;
        }
        return [...accumulator, { sessionType, targetId, targetName, session }];
      }, []);

    return valid.map(({ sessionType, targetId, targetName, session }, index) => {
      const size = getDefaultWindowSize(sessionType);
      return {
        id: session.id,
        sessionType,
        targetId,
        targetName,
        cognitoUsername: session.cognitoUsername,
        url: session.url,
        startedAt: session.startedAt,
        expiresAt: session.expiresAt,
        isMinimized: true,
        isActive: false,
        position: getInitialWindowPosition(sessionType, index, size),
        size,
        balloonPosition: getInitialBalloonPosition(index),
        zIndex: nextZIndex(),
      };
    });
  } catch {
    return [];
  }
}
